import { type FC, type HTMLAttributes, type PropsWithChildren, useEffect, useRef, useState } from 'react';

import { transitionHeightRecipe, transitionOpacityRecipe, transitionSlideRecipe } from './Transition.css';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
import { getClassNames } from '../../util/css';

interface TransitionOpacityProps extends HTMLAttributes<HTMLDivElement> {
  in?: boolean;
}

export const TransitionOpacity: FC<PropsWithChildren<TransitionOpacityProps>> = ({
  in: inProp,
  className,
  children,
  ...rest
}) => {
  return (
    <div className={getClassNames(transitionOpacityRecipe({ in: inProp }), className)} {...rest}>
      {children}
    </div>
  );
};

interface TransitionSlideProps {
  isEntering?: boolean;
  isAbsolute?: boolean;
  onEnterFinished?: () => void;
  onLeaveFinished?: () => void;
}

export const TransitionSlide: FC<PropsWithChildren<TransitionSlideProps>> = ({
  isEntering,
  isAbsolute,
  onEnterFinished,
  onLeaveFinished,
  children,
}) => {
  return (
    <div
      className={getClassNames(transitionSlideRecipe({ isEntering, absolute: isAbsolute }))}
      onAnimationEnd={isEntering ? onEnterFinished : onLeaveFinished}>
      {children}
    </div>
  );
};

interface TransitionHeightProps {
  removeMargin?: boolean;
  hideWhenCollapsed?: boolean;
  startHeight?: string;
  endHeight?: string;
  in?: boolean;
}

const TransitionHeightChild: FC<PropsWithChildren<TransitionHeightProps>> = ({
  removeMargin,
  hideWhenCollapsed,
  startHeight,
  endHeight,
  in: inProp,
  children,
}) => {
  const transitionRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!transitionRef.current) {
      return;
    }

    const style = transitionRef.current.style;
    const offsetHeight = transitionRef.current.scrollHeight ? `${transitionRef.current.scrollHeight}px` : undefined;

    style.setProperty('--transition-height-start', startHeight ?? offsetHeight ?? 'auto');
    style.setProperty('--transition-height-end', endHeight ?? '0');
  }, [endHeight, inProp, startHeight]);

  return (
    <div
      ref={transitionRef}
      className={getClassNames(transitionHeightRecipe({ in: inProp, removeMargin, hideWhenCollapsed }))}>
      {children}
    </div>
  );
};

export const TransitionHeight: FC<PropsWithChildren<TransitionHeightProps>> = props => {
  const [showChild, setShowChild] = useState(false);

  // Wait until after client-side hydration to show
  useEffect(() => {
    setShowChild(true);
  }, []);

  if (!showChild) {
    return <div>{props.children}</div>;
  }

  return <TransitionHeightChild {...props} />;
};
